const isLocalhost =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

const API_BASE =
  process.env.REACT_APP_API_BASE ??
  (process.env.NODE_ENV === "production" && !isLocalhost
    ? ""
    : "http://localhost:5000");

const handle = async response => {
  if (!response.ok) {
    let errorMessage = "Request failed";
    let errorData = {};
    try {
      errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      errorMessage = await response.text();
    }
    const error = new Error(errorMessage);
    // Preserve additional error properties like requiresVerification
    if (errorData.requiresVerification !== undefined) {
      error.requiresVerification = errorData.requiresVerification;
    }
    if (errorData.cooldown !== undefined) {
      error.cooldown = errorData.cooldown;
    }
    throw error;
  }
  return response.json();
};

export const api = {
  // ===== PUBLIC =====
  getCategories: () =>
    fetch(`${API_BASE}/api/public/categories`).then(handle),

  getCategoryProducts: slug =>
    fetch(`${API_BASE}/api/public/categories/${slug}/products`).then(handle),

  getProduct: id =>
    fetch(`${API_BASE}/api/public/products/${id}`).then(handle),

  searchProducts: query =>
    fetch(`${API_BASE}/api/public/search?q=${encodeURIComponent(query)}`).then(handle),

  // ===== AUTH =====
  login: (email, password) =>
    fetch(`${API_BASE}/api/public/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password })
    }).then(handle),

  signup: (name, email, password) =>
    fetch(`${API_BASE}/api/public/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, password })
    }).then(handle),

  resendVerification: (email) =>
    fetch(`${API_BASE}/api/public/resend-verification`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email })
    }).then(handle),

  forgotPassword: (email) =>
    fetch(`${API_BASE}/api/public/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email })
    }).then(handle),

  resetPassword: (token, newPassword) =>
    fetch(`${API_BASE}/api/public/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, newPassword })
    }).then(handle),

  updatePassword: (currentPassword, newPassword) => {
    const token = localStorage.getItem("authToken");
    return fetch(`${API_BASE}/api/public/update-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ currentPassword, newPassword })
    }).then(handle);
  },

  checkAdmin: () => {
    const token = localStorage.getItem("authToken");
    return fetch(`${API_BASE}/api/public/check-admin`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).then(handle);
  },

  // ===== ORDERS =====
  getMyOrders: () => {
    const token = localStorage.getItem("authToken");
    return fetch(`${API_BASE}/api/public/my-orders`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(handle);
  },

  createOrder: formData => {
    const token = localStorage.getItem("authToken");
    return fetch(`${API_BASE}/api/public/create-order`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    }).then(handle);
  },

  // Razorpay payment
  createRazorpayOrder: (amount, currency = "USD") => {
    const token = localStorage.getItem("authToken");
    return fetch(`${API_BASE}/api/public/create-razorpay-order`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ amount, currency })
    }).then(handle);
  },

  verifyRazorpayPayment: (paymentData) => {
    const token = localStorage.getItem("authToken");
    return fetch(`${API_BASE}/api/public/verify-razorpay-payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(paymentData)
    }).then(handle);
  },

  // ===== DEALERSHIP =====
  submitDealershipApplication: (formData) =>
    fetch(`${API_BASE}/api/dealership/submit-application`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData)
    }).then(handle),

  // ===== ADMIN =====
  getAdminProducts: () =>
    fetch(`${API_BASE}/api/admin/products`).then(handle),

  createProduct: formData =>
    fetch(`${API_BASE}/api/admin/products`, {
      method: "POST",
      body: formData
    }).then(handle),

  updateProduct: (id, formData) =>
    fetch(`${API_BASE}/api/admin/products/${id}`, {
      method: "PUT",
      body: formData
    }).then(handle),

  // Fetch a product for editing (includes variants grouped with attributes)
  getProductForEdit: (id) =>
    fetch(`${API_BASE}/api/admin/products/${id}/edit`).then(handle),

  deleteProduct: id =>
    fetch(`${API_BASE}/api/admin/products/${id}`, {
      method: "DELETE"
    }).then(handle),

  deleteVariant: id =>
    fetch(`${API_BASE}/api/admin/variants/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`
      }
    }).then(handle),

  bulkDeleteVariants: (variantIds) =>
    fetch(`${API_BASE}/api/admin/variants/bulk-delete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify({ variantIds })
    }).then(handle),

  // Update a single variant's editable fields (sku, price, stock)
  updateVariant: (variantId, data) => {
    const token = localStorage.getItem("authToken");
    return fetch(`${API_BASE}/api/admin/variants/${variantId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    }).then(handle);
  },

  updateProductStatus: (id, isActive) => {
    const token = localStorage.getItem("authToken");
    return fetch(`${API_BASE}/api/admin/products/${id}/status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ isActive })
    }).then(handle);
  },

  // Price increase functions
  increaseAllPrices: (percentage) => {
    const token = localStorage.getItem("authToken");
    return fetch(`${API_BASE}/api/admin/products/increase-prices/all`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ percentage })
    }).then(handle);
  },

  increaseProductPrices: (productId, percentage) => {
    const token = localStorage.getItem("authToken");
    return fetch(`${API_BASE}/api/admin/products/increase-prices/product/${productId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ percentage })
    }).then(handle);
  },

  increaseCategoryPrices: (categoryId, percentage) => {
    const token = localStorage.getItem("authToken");
    return fetch(`${API_BASE}/api/admin/products/increase-prices/category/${categoryId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ percentage })
    }).then(handle);
  },

  // Admin Categories
  createCategory: formData =>
    fetch(`${API_BASE}/api/admin/categories`, {
      method: "POST",
      body: formData
    }).then(handle),

  updateCategory: (categoryId, formData) =>
    fetch(`${API_BASE}/api/admin/categories/${categoryId}`, {
      method: "PUT",
      body: formData
    }).then(handle),

  deleteCategory: id =>
    fetch(`${API_BASE}/api/admin/categories/${id}`, {
      method: "DELETE"
    }).then(handle),

  // Admin Orders
  getAllOrders: () => {
    const token = localStorage.getItem("authToken");
    return fetch(`${API_BASE}/api/admin/orders`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).then(handle);
  },

  updateOrderStatus: (id, status, cancellationReason) => {
    const token = localStorage.getItem("authToken");
    const body = { status };
    if (cancellationReason) {
      body.cancellation_reason = cancellationReason;
    }
    return fetch(`${API_BASE}/api/admin/orders/${id}/status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    }).then(handle);
  },

  // Restaurant orders (public, external)
  getRestaurantOrders: async () => {
    // Try direct fetch first
    const directUrl = "https://rkindustriesexports.com/api/admin/restaurant-orders";
    try {
      const res = await fetch(directUrl);
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      return await res.json();
    } catch (err) {
      // Likely CORS; fallback via public CORS-friendly relay
      const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(directUrl)}`;
      const res = await fetch(proxyUrl);
      if (!res.ok) throw new Error(`Fallback failed with status ${res.status}`);
      return await res.json();
    }
  },

  // Contact form
  submitContact: (name, email, message) =>
    fetch(`${API_BASE}/api/public/contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, message })
    }).then(handle)
};

export { API_BASE };
